<template>
  <div class="family-group-page">
    <div class="page-title">
      <img src="@/assets/icons/everyone_500.png" class="page-title-icon"/>
      <div class="page-title-text-area" :class="{ 'has-name': hasNameRef }">
        <h1 class="with-subhead">{{ title }}</h1>
        <p class="subhead">
          Group managed by <UserLink :user-id="familyGroupRef?.ownerId" dimmed/>
        </p>
      </div>
    </div>
    <ResultIndicator ref="createSuccessAlertRef" alert-type="success">
      New group created.
    </ResultIndicator>
    <div>
      <ul ref="tabsElemRef" class="nav nav-pills" role="tablist">
        <li class="nav-item">
          <button type="button" id="membersTab" class="nav-link active" data-bs-toggle="tab" data-bs-target="#membersPane" role="tab" aria-controls="membersPane" aria-selected="true">
            People
            <span v-if="membersRef.length > 0" class="count">{{ membersRef.length }}</span>
          </button>
        </li>
        <li class="nav-item">
          <button type="button" id="researchGroupsTab" class="nav-link" data-bs-toggle="tab" data-bs-target="#researchGroupsPane" role="tab" aria-controls="researchGroupsPane" aria-selected="true">
            Shared trees
            <span v-if="membersRef.length > 0" class="count">{{ sharedResearchGroupsRef.length }}</span>
          </button>
        </li>
        <li class="nav-item">
          <button type="button" id="manageTab" class="nav-link" data-bs-toggle="tab" data-bs-target="#managePane" role="tab" aria-controls="managePane" aria-selected="false">
            Manage
          </button>
        </li>
      </ul>
    </div>
    <div class="tab-content content-area">
      <section id="membersPane" class="tab-pane show active" role="tabpanel" aria-labelledby="membersTab" tabindex="0">
        <div class="scope-area">
          <h5 class="area-header">
            Common ancestors
          </h5>
          <FamilyGroupScope ref="scopeRef" 
            :family-group-id="familyGroupId"
            :show-step="showScopeStepRef"
            @loaded="scopeLoadedRef = true"
            @step-dismissed="scopeStepDismissedRef = true"/>
        </div>
        <div v-if="scopeLoadedRef && !showScopeStepRef" class="members-area">
          <h5 class="area-header">
            Group members
          </h5>
          <FamilyGroupMemberList ref="memberListRef" 
            :family-group-id="props.familyGroupId"
            :disable-list-actions="showScopeStepRef"/>
        </div>
      </section>
      <section id="researchGroupsPane" class="tab-pane" role="tabpanel" aria-labelledby="researchGroupsTab" tabindex="0">
        <div class="area-body">
          <FamilyGroupSharedResearchGroups :family-group-id="familyGroupId" @choose-members="onChooseMembers"/>
        </div>
      </section>
      <section id="managePane" class="tab-pane" role="tabpanel" aria-labelledby="manageTab" tabindex="0">
        <FamilyGroupManage :family-group-id="familyGroupId"/>
      </section>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.page-title {

  .page-title-icon {
    margin-top: 0.5rem;
    margin-right: 0.75rem;
    width: 55px;
  }

  .page-title-text-area {
    &.has-name h1 {
      font-size: 1.7rem; // smaller to accommodate long group names
    }

    &:not(.has-name) .subhead {
      display: none;
    }
  }
}

.nav {
  max-width: 800px;
}

.area-header {
  font-size: 1.15rem;
}

.area-body {
  margin: 1rem 0 2rem;
}

.actions {
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1rem;
  font-size: 0.875rem;
}

#membersPane.show {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}
</style>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { usePageTitle } from '@/util/AppUtil'
import { ItemPermissions } from '@/gp/GroupAdminModel'
import { useFamilyGroupStore } from '@/gp/FamilyGroupStore'
import { useFamilyGroupMemberStore } from '@/gp/FamilyGroupMemberStore'
import { useDataGroupMemberStore } from '@/gp/DataGroupMemberStore'
import { useDataGroupStore } from '@/gp/DataGroupStore'
import { LoadMode } from '@/util/AsyncData'
import { useInitialTabRouting } from '@/util/InitialTabRouting'
import { useTabsTelemetry } from '@/util/TabTelemetry'
import { Tab } from 'bootstrap'
import UserLink from '@/manage/users/UserLink.vue'
import ResultIndicator from '@/util/ResultIndicator.vue'
import FamilyGroupScope from './FamilyGroupScope.vue'
import FamilyGroupMemberList from './FamilyGroupMemberList.vue'
import FamilyGroupSharedResearchGroups from './FamilyGroupSharedResearchGroups.vue'
import FamilyGroupManage from './FamilyGroupManage.vue'

const props = defineProps<{
  familyGroupId: string
}>()

const emit = defineEmits<{
  (e: 'chooseMembers'): void
}>()

const tabsElemRef = ref<HTMLElement>()
const scopeLoadedRef = ref(false)
const scopeStepDismissedRef = ref(false)
const showSuggestedRef = ref(false)

const createSuccessAlertRef = ref<InstanceType<typeof ResultIndicator>>()
const scopeRef = ref<InstanceType<typeof FamilyGroupScope>>()
const memberListRef = ref<InstanceType<typeof FamilyGroupMemberList>>()

const familyGroupStore = useFamilyGroupStore()
const familyGroupMemberStore = useFamilyGroupMemberStore()
const dataGroupMemberStore = useDataGroupMemberStore()
const dataGroupStore = useDataGroupStore()

const familyGroupRef = computed(() => familyGroupStore.getAsyncGroup(props.familyGroupId, LoadMode.EnsureLoaded)?.data)
const permissionsRef = computed(() => familyGroupStore.getAsyncPermissions(props.familyGroupId, LoadMode.EnsureLoaded)?.data || ItemPermissions.None)
const isOwnerRef = computed(() => (permissionsRef.value & ItemPermissions.Owner) > 0)
const hasNameRef = computed(() => !!familyGroupRef.value?.name)
const title = computed(() => familyGroupRef.value?.name ?? "New group")
const membersRef = computed(() => familyGroupMemberStore.getMembersForFamilyGroups([props.familyGroupId], LoadMode.EnsureLoaded))
const sharedDataGroupsRef = computed(() => {
  const sharedDataGroupIds = dataGroupMemberStore.getMembersForFamilyGroups([props.familyGroupId], LoadMode.EnsureLoaded)
    .map(m => m.dataGroupId)
  return dataGroupStore.getLoadedGroupList(sharedDataGroupIds) // loaded with members
})
const sharedResearchGroupsRef = computed(() => sharedDataGroupsRef.value.filter(dg => !dg.isProfile))
const sharedProfilesRef = computed(() => sharedDataGroupsRef.value.filter(dg => dg.isProfile))
const showScopeStepRef = computed(() => !scopeStepDismissedRef.value && !familyGroupRef.value?.scopeFamilyId && isOwnerRef.value && sharedProfilesRef.value.length <= 1) // owner only
  
usePageTitle("Group", () => familyGroupRef.value?.name)
useInitialTabRouting('membersTab')
useTabsTelemetry(tabsElemRef)

watch(() => props.familyGroupId, () => scopeLoadedRef.value = false)

onMounted(() => {
  scopeStepDismissedRef.value = false // reset on page load

  const createdDate = familyGroupRef.value?.createdDate
  if (createdDate && Math.abs(createdDate.diffNow('seconds').seconds) < 30) {
    createSuccessAlertRef.value?.show()
  }
})

watch(familyGroupRef, () => {
  showSuggestedRef.value = !!familyGroupRef.value?.scopeFamilyId
})

function onChooseMembers() {
  new Tab(document.getElementById("membersTab")!).show()
}
</script>
